import { byDesc, sortByComparisons } from "@lyfegen-platform/utils";
import { FlexFieldController } from "../models/flexible/flex-field-controller";
import { AgreementScope, Article71Indication } from "../models/generated/graphql";
import { distinctBy } from "../utils/distinct-by";
import { useScopes } from "./useScopes";
import { getScopePreparations } from "../utils/agreement-utils";
import { useFieldValue } from "formik";
import { combinePaths } from "../models/flexible/utils";
import { getPathPrefix } from "../utils/get-path-prefix";
import { AttributeIdentifiers } from "@lyfegen-platform/graphql-schema/models";
import { ExtendedArticle71Indication } from "../controllers/relevant-indication-field-controller";

const getRelevantIndicationsFromScopes = (scopes: Array<AgreementScope>, genericValue: any): Array<Article71Indication> => {
	const availableRelevantIndications = scopes
		.flatMap((scope) => getScopePreparations(scope, (treatmentSet) => !genericValue || treatmentSet.generic.Name === genericValue.Name))
		.map((p) => p.brand)
		.flatMap((brand) => brand.article71Indications)
		.filter((ind) => !!ind.name && !!ind.id);

	return distinctBy(availableRelevantIndications, (relevantIndication) => relevantIndication.id);
};

export const useAvailableRelevantIndications = (fieldController: FlexFieldController, path: string) => {
	const [genericValue] = useFieldValue(combinePaths(getPathPrefix(path, 1), AttributeIdentifiers.GenericName));
	const { selectedScopes } = useScopes(fieldController.parent.context);

	const selectedScopeRelevantIndications = getRelevantIndicationsFromScopes(selectedScopes, genericValue);

	const sortedRelevantIndications = selectedScopeRelevantIndications.sort(
		sortByComparisons(
			byDesc((ri) => !!ri.mainIndication),
			byDesc((ri) => ri.mainIndication?.validFromDate),
			byDesc((ri) => ri.id)
		)
	);

	if (sortedRelevantIndications.length === 0) {
		return { options: [] };
	}

	const lastMainIndicationIndex = sortedRelevantIndications.findIndex((ri) => !ri.mainIndication) - 1;

	if (lastMainIndicationIndex < -1) {
		return {
			options: sortedRelevantIndications,
		};
	}

	const sortedIndicationsWithAppendix = sortedRelevantIndications.map((ri, index) =>
		index === lastMainIndicationIndex ? ({ ...ri, appendix: " " } as ExtendedArticle71Indication) : ri
	);

	return {
		options: sortedIndicationsWithAppendix,
	};
};
